import { BUTTON_LABELS, TABLE_LABELS } from '@/constants/labels';
import { getSkillLevel } from '@/domains';
import { IManagedUserRecentSummary, IManagedUserSession } from '@/types';
import { ManagerTableCellLabel as Label } from '../ManagerTableCellLabel';
import { ManagerTableLinkButton as LinkButton } from '../ManagerTableLinkButton';
import { SkillLevelTag } from '@/components/Tags';
import { useMemo } from 'react';

interface RenderProps {
  mostRecentRoleplay: IManagedUserSession | null;
  onViewScorecardClick: (userId: string, data: IManagedUserSession) => void;
  record: IManagedUserRecentSummary;
}

/**
 * Roleplay status cell for the manager dashboard table. This component is responsible for 
 * displaying the roleplay status for a user, along with any relevant actions (view scorecard).
 * 
 * The logic for displaying the roleplay status is as follows:
 *  - If there is no shared role-play, we display "None shared"
 *  - If there is a shared role-play but no attempts, we display "No attempts"
 *  - If there is a shared role-play with attempts, we determine the skill 
 *    level based on the most recent attempt's score and display the skill level tag
 *    along with a view button to view the scorecard for that role-play attempt.
 */
export const ManagerTableCellRoleplayStatus: React.FC<RenderProps> = ({
  mostRecentRoleplay,
  onViewScorecardClick,
  record,
}) => {
  /**
   * If there is a shared role-play with attempts, we determine the skill 
   * level based on the most recent attempt's score.
   */
  const skillLevel = useMemo(() => {
    if (!mostRecentRoleplay) return null;

    if (typeof mostRecentRoleplay.scorePercentage === 'number') {
      return getSkillLevel(mostRecentRoleplay.scorePercentage);
    }

    return getSkillLevel(mostRecentRoleplay.score);
  }, [mostRecentRoleplay]);

  // If there is no shared role-play, we display "None shared"
  if (mostRecentRoleplay === null) {
    return <Label label={TABLE_LABELS.NONE_SHARED} />;
  }

  // if there is a shared role-play but no attempts, we display "No attempts"
  if (mostRecentRoleplay.attemptCount === 0) {
    return <Label label={TABLE_LABELS.NO_ATTEMPTS} />;
  }

  // If there is a shared role-play with attempts,
  // we display the skill level tag and a view button
  if (!skillLevel || skillLevel === null) {
    return (
      <div className="flex items-center justify-start gap-2">
        <Label label={TABLE_LABELS.UNAVAILABLE} />
      </div>
    );
  }

  // ==============================================
  // Rendering logic
  // ==============================================
  return (
    <div className="flex items-center justify-start gap-2">
      <SkillLevelTag skillLevel={skillLevel} />
      <LinkButton
        label={BUTTON_LABELS.VIEW}
        onClick={() => onViewScorecardClick(record.userId, mostRecentRoleplay)}
      />
    </div>
  );
};

ManagerTableCellRoleplayStatus.displayName = 'ManagerTableCellRoleplayStatus';

export default ManagerTableCellRoleplayStatus;
